import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../lib/userSystem';
import {
  Users, Plus, Shield, ShieldCheck, ShieldAlert,
  Check, X, Loader2, AlertCircle, UserPlus, Eye, EyeOff,
} from 'lucide-react';

export default function UserManagement() {
  const { fetchAllProfiles, createUser, updateUserRole, deactivateUser, profile: currentProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'reception' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const profiles = await fetchAllProfiles();
    setUsers(profiles);
    setLoading(false);
  }, [fetchAllProfiles]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setFormError('All fields are required.');
      return;
    }
    if (formData.password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);
    const result = await createUser(formData.email.trim(), formData.name.trim(), formData.role, formData.password);
    setIsSubmitting(false);

    if (result.success) {
      setFormSuccess(`Account created for ${formData.name}. Share the app link (https://errigal-events.vercel.app/) and their password with them — they can sign in immediately.`);
      setFormData({ name: '', email: '', password: '', role: 'reception' });
      setShowAddForm(false);
      loadUsers();
    } else {
      if (result.error?.includes('already registered')) {
        setFormError('This email is already registered.');
      } else {
        setFormError(result.error || 'Failed to create user.');
      }
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentProfile?.id) return; // Can't change own role
    const result = await updateUserRole(userId, newRole);
    if (result.success) {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    }
  };

  const handleDeactivate = async (userId) => {
    if (userId === currentProfile?.id) return;
    if (!window.confirm('Are you sure you want to deactivate this user? They will no longer be able to sign in.')) return;
    const result = await deactivateUser(userId);
    if (result.success) {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: false } : u)));
    }
  };

  const roleIcons = {
    reception: Shield,
    manager: ShieldCheck,
    admin: ShieldAlert,
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">Manage team accounts and roles</p>
        </div>
        <button
          onClick={() => { setShowAddForm(!showAddForm); setFormError(''); setFormSuccess(''); }}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition w-full md:w-auto min-h-[44px]"
        >
          <UserPlus size={18} />
          Add User
        </button>
      </div>

      {/* Success Banner */}
      {formSuccess && (
        <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
          <Check size={16} className="text-emerald-600" />
          <p className="text-sm text-emerald-700">{formSuccess}</p>
          <button onClick={() => setFormSuccess('')} className="ml-auto text-emerald-400 hover:text-emerald-600">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Add User Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 mb-6">
          <h2 className="font-bold text-gray-900 mb-4">Create New Account</h2>

          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              <p className="text-sm text-red-700">{formError}</p>
            </div>
          )}

          <form onSubmit={handleAddUser} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="Sarah Kelly"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                placeholder="sarah@errigal.ie"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Min 6 characters"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">The user can change this after first login</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData((p) => ({ ...p, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {Object.entries(ROLES).map(([key, r]) => (
                  <option key={key} value={key}>{r.label} — {r.description}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {isSubmitting ? 'Creating...' : 'Create Account'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 size={24} className="animate-spin text-gray-400 mx-auto" />
            <p className="text-sm text-gray-500 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={32} className="text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No users yet. Add your first team member above.</p>
          </div>
        ) : (
          <table className="w-full min-w-max">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 md:px-6 py-3">User</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 md:px-6 py-3">Role</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 md:px-6 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 md:px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const roleInfo = ROLES[user.role] || ROLES.reception;
                const RoleIcon = roleIcons[user.role] || Shield;
                const isCurrentUser = user.id === currentProfile?.id;

                return (
                  <tr key={user.id} className={`border-b border-gray-100 last:border-0 ${!user.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-3 md:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                          style={{ backgroundColor: roleInfo.colour }}
                        >
                          {user.initials || '?'}
                        </div>
                        <div className="hidden sm:block">
                          <p className="text-sm font-medium text-gray-900">
                            {user.name}
                            {isCurrentUser && <span className="text-xs text-gray-400 ml-2">(you)</span>}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4">
                      {isCurrentUser ? (
                        <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: roleInfo.colour }}>
                          <RoleIcon size={14} />
                          {roleInfo.label}
                        </span>
                      ) : (
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={!user.is_active}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          {Object.entries(ROLES).map(([key, r]) => (
                            <option key={key} value={key}>{r.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                          <Check size={12} /> <span className="hidden sm:inline">Active</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          <X size={12} /> <span className="hidden sm:inline">Deactivated</span>
                        </span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 text-right min-w-[80px]">
                      {!isCurrentUser && user.is_active && (
                        <button
                          onClick={() => handleDeactivate(user.id)}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Info */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 mb-2">
          <strong>How accounts work:</strong>
        </p>
        <ul className="text-sm text-blue-800 space-y-1 ml-4">
          <li>1. Create a new account with their email, a password, and a role</li>
          <li>2. Share the app link and their password with them directly (WhatsApp, text, in person)</li>
          <li>3. They sign in immediately — no email confirmation needed</li>
          <li>4. They can change their password after first login</li>
          <li>Only Admins can create accounts and change roles</li>
        </ul>
      </div>
    </div>
  );
}
